import { memo, useMemo } from 'react'
import { LineChart } from '@mui/x-charts/LineChart'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import { FormattedMessage, useIntl } from 'react-intl'
import { map, sortBy, isEqual } from 'lodash'

const CHART_MARGIN = { left: 70, right: 20, top: 20, bottom: 30 }
const LEGEND_SLOT_PROPS = { hidden: true }
const TOOLTIP_SLOT_PROPS = { trigger: 'axis' }

function StatisticsAreaChart({
  title,
  titleId,
  data,
  loading,
  yAxisLabel,
  formatValue,
  height = 300
}) {
  const { formatDate, formatTime } = useIntl()

  // Sort data by timestamp and prepare for chart
  const { chartData, xLabels } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [], xLabels: [] }
    }

    // Sort by bucket timestamp
    const sorted = sortBy(data, (item) => new Date(item.bucket).getTime())

    const labels = map(sorted, (item) => {
      const d = new Date(item.bucket)
      return `${formatDate(d, { month: 'short', day: 'numeric' })} ${formatTime(d, { hour: '2-digit', minute: '2-digit' })}`
    })

    return {
      chartData: sorted,
      xLabels: labels,
    }
  }, [data, formatDate, formatTime])

  const valueFormatter = useMemo(() => {
    if (formatValue) return formatValue
    return (value) => value != null ? `${value.toFixed(1)}%` : ''
  }, [formatValue])

  const xAxis = useMemo(() => {
    const step = Math.ceil(xLabels.length / 6)
    return [{
      data: xLabels,
      scaleType: 'point',
      tickLabelInterval: (_value, index) => index % step === 0,
    }]
  }, [xLabels])

  const yAxis = useMemo(() => [{
    label: yAxisLabel,
    valueFormatter,
  }], [yAxisLabel, valueFormatter])

  const series = useMemo(() => [
    {
      data: map(chartData, 'max'),
      label: 'Max',
      showMark: false,
      color: 'rgba(25, 118, 210, 0.3)',
      valueFormatter,
    },
    {
      data: map(chartData, 'avg'),
      label: 'Avg',
      showMark: false,
      color: '#1976d2',
      valueFormatter,
    },
    {
      data: map(chartData, 'min'),
      label: 'Min',
      showMark: false,
      color: 'rgba(25, 118, 210, 0.3)',
      valueFormatter,
    },
  ], [chartData, valueFormatter])

  const slotProps = useMemo(() => ({
    legend: LEGEND_SLOT_PROPS,
    tooltip: TOOLTIP_SLOT_PROPS,
  }), [])

  if (loading) {
    return (
      <Box sx={{ width: '100%', mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {titleId ? <FormattedMessage id={titleId} /> : title}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
          <CircularProgress />
        </Box>
      </Box>
    )
  }

  if (!data || data.length === 0 || chartData.length === 0) {
    return (
      <Box sx={{ width: '100%', mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {titleId ? <FormattedMessage id={titleId} /> : title}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
          <Typography color="text.secondary">
            <FormattedMessage id="charts.noData" />
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        {titleId ? <FormattedMessage id={titleId} /> : title}
      </Typography>
      <LineChart
        height={height}
        xAxis={xAxis}
        yAxis={yAxis}
        series={series}
        slotProps={slotProps}
        margin={CHART_MARGIN}
      />
    </Box>
  )
}

export default memo(StatisticsAreaChart, (prevProps, nextProps) => {
  return (
    prevProps.loading === nextProps.loading &&
    prevProps.title === nextProps.title &&
    prevProps.titleId === nextProps.titleId &&
    prevProps.height === nextProps.height &&
    prevProps.yAxisLabel === nextProps.yAxisLabel &&
    prevProps.formatValue === nextProps.formatValue &&
    isEqual(prevProps.data, nextProps.data)
  )
})
